// src/redux/reducers/timesheetReducer.js
import {
    SET_TIMESHEET_LOADING,
    CLEAR_TIMESHEET_ERROR,

    FETCH_TIMESHEETS_SUCCESS,
    FETCH_TIMESHEETS_FAIL,

    GET_TIMESHEET_SUCCESS,
    GET_TIMESHEET_FAIL,

    CREATE_TIMESHEET_SUCCESS,
    CREATE_TIMESHEET_FAIL,

    UPDATE_TIMESHEET_SUCCESS,
    UPDATE_TIMESHEET_FAIL,

    DELETE_TIMESHEET_SUCCESS,
    DELETE_TIMESHEET_FAIL,

    FETCH_TIMESHEET_DAYS_SUCCESS,
    FETCH_TIMESHEET_DAYS_FAIL,

    UPSERT_TIMESHEET_DAY_SUCCESS,
    UPSERT_TIMESHEET_DAY_FAIL,

    DELETE_TIMESHEET_DAY_SUCCESS,
    DELETE_TIMESHEET_DAY_FAIL,

    FETCH_TIMESHEET_OVERTIMES_SUCCESS,
    FETCH_TIMESHEET_OVERTIMES_FAIL,

    UPSERT_TIMESHEET_OVERTIME_SUCCESS,
    UPSERT_TIMESHEET_OVERTIME_FAIL,

    DELETE_TIMESHEET_OVERTIME_SUCCESS,
    DELETE_TIMESHEET_OVERTIME_FAIL,

    // Overtime Workflow
    SUBMIT_OVERTIME_SUCCESS,
    SUBMIT_OVERTIME_FAIL,

    FETCH_OVERTIME_MANAGER_INBOX_SUCCESS,
    FETCH_OVERTIME_MANAGER_INBOX_FAIL,
    FETCH_OVERTIME_MANAGER_HISTORY_SUCCESS,
    FETCH_OVERTIME_MANAGER_HISTORY_FAIL,

    FETCH_OVERTIME_ADMIN_INBOX_SUCCESS,
    FETCH_OVERTIME_ADMIN_INBOX_FAIL,
    FETCH_OVERTIME_ADMIN_HISTORY_SUCCESS,
    FETCH_OVERTIME_ADMIN_HISTORY_FAIL,

    MANAGER_OVERTIME_DECISION_SUCCESS,
    MANAGER_OVERTIME_DECISION_FAIL,

    ADMIN_OVERTIME_DECISION_SUCCESS,
    ADMIN_OVERTIME_DECISION_FAIL,
} from "../actions/timesheetActions";

const initialState = {
    timesheets: [],
    currentTimesheet: null,

    // { [timesheetId]: TimesheetDayResponseDTO[] }
    daysByTimesheetId: {},

    // { [timesheetId]: TimesheetOvertimeResponseDTO[] }
    overtimesByTimesheetId: {},

    // Overtime workflow lists
    overtimeManagerInbox: [],
    overtimeManagerHistory: [],
    overtimeAdminInbox: [],
    overtimeAdminHistory: [],

    loading: false,
    error: null,
};

// Helper: update an overtime in overtimesByTimesheetId
function updateOvertimeInMap(state, updatedOT) {
    const tid = updatedOT?.timesheetId;
    if (!tid) return state.overtimesByTimesheetId;
    const prev = state.overtimesByTimesheetId[tid] || [];
    const idx = prev.findIndex(x => x.id === updatedOT.id);
    const next = idx >= 0 ? prev.map((x, i) => (i === idx ? updatedOT : x)) : [updatedOT, ...prev];
    return { ...state.overtimesByTimesheetId, [tid]: next };
}

// Helper: update an overtime in a flat list
function updateOvertimeInList(list, updatedOT) {
    const idx = list.findIndex(x => x.id === updatedOT.id);
    if (idx >= 0) return list.map((x, i) => (i === idx ? updatedOT : x));
    return list;
}

export default function timesheetReducer(state = initialState, action) {
    switch (action.type) {
        case SET_TIMESHEET_LOADING:
            return { ...state, loading: action.payload };

        case CLEAR_TIMESHEET_ERROR:
            return { ...state, error: null };

        // ===== LIST =====
        case FETCH_TIMESHEETS_SUCCESS:
            return { ...state, timesheets: action.payload || [], error: null };
        case FETCH_TIMESHEETS_FAIL:
            return { ...state, error: action.payload };

        // ===== GET ONE =====
        case GET_TIMESHEET_SUCCESS:
            return { ...state, currentTimesheet: action.payload, error: null };
        case GET_TIMESHEET_FAIL:
            return { ...state, error: action.payload };

        // ===== CREATE =====
        case CREATE_TIMESHEET_SUCCESS: {
            const created = action.payload;
            return {
                ...state,
                timesheets: [created, ...state.timesheets.filter((t) => t.id !== created?.id)],
                currentTimesheet: created,
                error: null,
            };
        }
        case CREATE_TIMESHEET_FAIL:
            return { ...state, error: action.payload };

        // ===== UPDATE =====
        case UPDATE_TIMESHEET_SUCCESS: {
            const updated = action.payload;
            return {
                ...state,
                timesheets: state.timesheets.map((t) => (t.id === updated?.id ? updated : t)),
                currentTimesheet: state.currentTimesheet?.id === updated?.id ? updated : state.currentTimesheet,
                error: null,
            };
        }
        case UPDATE_TIMESHEET_FAIL:
            return { ...state, error: action.payload };

        // ===== DELETE =====
        case DELETE_TIMESHEET_SUCCESS: {
            const id = action.payload;
            const next = state.timesheets.filter((t) => t.id !== id);
            const current = state.currentTimesheet?.id === id ? null : state.currentTimesheet;
            const days = { ...state.daysByTimesheetId };
            delete days[id];
            return { ...state, timesheets: next, currentTimesheet: current, daysByTimesheetId: days, error: null };
        }
        case DELETE_TIMESHEET_FAIL:
            return { ...state, error: action.payload };

        // ===== DAYS LIST =====
        case FETCH_TIMESHEET_DAYS_SUCCESS: {
            const { timesheetId, days } = action.payload || {};
            return {
                ...state,
                daysByTimesheetId: { ...state.daysByTimesheetId, [timesheetId]: days || [] },
                error: null,
            };
        }
        case FETCH_TIMESHEET_DAYS_FAIL:
            return { ...state, error: action.payload };

        // ===== UPSERT DAY =====
        case UPSERT_TIMESHEET_DAY_SUCCESS: {
            const row = action.payload;
            const tid = row?.timesheetId;
            if (!tid) return state;

            const prev = state.daysByTimesheetId[tid] || [];
            const idx = prev.findIndex((x) => x.id === row.id || (x.workDate === row.workDate));
            let next;
            if (idx >= 0) next = prev.map((x, i) => (i === idx ? row : x));
            else next = [row, ...prev];

            return {
                ...state,
                daysByTimesheetId: { ...state.daysByTimesheetId, [tid]: next },
                error: null,
            };
        }
        case UPSERT_TIMESHEET_DAY_FAIL:
            return { ...state, error: action.payload };

        // ===== DELETE DAY =====
        case DELETE_TIMESHEET_DAY_SUCCESS: {
            const { dayId, timesheetId } = action.payload || {};
            const prev = state.daysByTimesheetId[timesheetId] || [];
            return {
                ...state,
                daysByTimesheetId: {
                    ...state.daysByTimesheetId,
                    [timesheetId]: prev.filter((d) => d.id !== dayId),
                },
                error: null,
            };
        }
        case DELETE_TIMESHEET_DAY_FAIL:
            return { ...state, error: action.payload };

        // ===== OVERTIMES =====
        case FETCH_TIMESHEET_OVERTIMES_SUCCESS: {
            const { timesheetId, overtimes } = action.payload || {};
            return {
                ...state,
                overtimesByTimesheetId: { ...state.overtimesByTimesheetId, [timesheetId]: overtimes || [] },
                error: null,
            };
        }
        case FETCH_TIMESHEET_OVERTIMES_FAIL:
            return { ...state, error: action.payload };

        case UPSERT_TIMESHEET_OVERTIME_SUCCESS: {
            const row = action.payload;
            const tid = row?.timesheetId;
            if (!tid) return state;

            const prev = state.overtimesByTimesheetId[tid] || [];
            const idx = prev.findIndex((x) => x.id === row.id);
            let next;
            if (idx >= 0) next = prev.map((x, i) => (i === idx ? row : x));
            else next = [row, ...prev];

            return {
                ...state,
                overtimesByTimesheetId: { ...state.overtimesByTimesheetId, [tid]: next },
                error: null,
            };
        }
        case UPSERT_TIMESHEET_OVERTIME_FAIL:
            return { ...state, error: action.payload };

        case DELETE_TIMESHEET_OVERTIME_SUCCESS: {
            const { overtimeId, timesheetId } = action.payload || {};
            const prev = state.overtimesByTimesheetId[timesheetId] || [];
            return {
                ...state,
                overtimesByTimesheetId: {
                    ...state.overtimesByTimesheetId,
                    [timesheetId]: prev.filter((o) => o.id !== overtimeId),
                },
                error: null,
            };
        }
        case DELETE_TIMESHEET_OVERTIME_FAIL:
            return { ...state, error: action.payload };

        // ===== OVERTIME WORKFLOW =====

        case SUBMIT_OVERTIME_SUCCESS: {
            const { data } = action.payload || {};
            if (!data) return { ...state, error: null };
            return {
                ...state,
                overtimesByTimesheetId: updateOvertimeInMap(state, data),
                error: null,
            };
        }
        case SUBMIT_OVERTIME_FAIL:
            return { ...state, error: action.payload };

        case FETCH_OVERTIME_MANAGER_INBOX_SUCCESS:
            return { ...state, overtimeManagerInbox: action.payload || [], error: null };
        case FETCH_OVERTIME_MANAGER_INBOX_FAIL:
            return { ...state, error: action.payload };

        case FETCH_OVERTIME_MANAGER_HISTORY_SUCCESS:
            return { ...state, overtimeManagerHistory: action.payload || [], error: null };
        case FETCH_OVERTIME_MANAGER_HISTORY_FAIL:
            return { ...state, error: action.payload };

        case FETCH_OVERTIME_ADMIN_INBOX_SUCCESS:
            return { ...state, overtimeAdminInbox: action.payload || [], error: null };
        case FETCH_OVERTIME_ADMIN_INBOX_FAIL:
            return { ...state, error: action.payload };

        case FETCH_OVERTIME_ADMIN_HISTORY_SUCCESS:
            return { ...state, overtimeAdminHistory: action.payload || [], error: null };
        case FETCH_OVERTIME_ADMIN_HISTORY_FAIL:
            return { ...state, error: action.payload };

        case MANAGER_OVERTIME_DECISION_SUCCESS: {
            const { data } = action.payload || {};
            if (!data) return state;
            return {
                ...state,
                overtimeManagerInbox: updateOvertimeInList(state.overtimeManagerInbox, data),
                overtimeManagerHistory: updateOvertimeInList(state.overtimeManagerHistory, data),
                overtimesByTimesheetId: updateOvertimeInMap(state, data),
                error: null,
            };
        }
        case MANAGER_OVERTIME_DECISION_FAIL:
            return { ...state, error: action.payload };

        case ADMIN_OVERTIME_DECISION_SUCCESS: {
            const { data } = action.payload || {};
            if (!data) return state;
            return {
                ...state,
                overtimeAdminInbox: updateOvertimeInList(state.overtimeAdminInbox, data),
                overtimeAdminHistory: updateOvertimeInList(state.overtimeAdminHistory, data),
                overtimesByTimesheetId: updateOvertimeInMap(state, data),
                error: null,
            };
        }
        case ADMIN_OVERTIME_DECISION_FAIL:
            return { ...state, error: action.payload };

        default:
            return state;
    }
}
